// File: src/bridge/omnicine/export/PodcastExportPanel.tsx
import * as React from "react";
import "@/bridge/omnicine/export/exportGlobal";

type ExportFormat = "json" | "wav";

type ExportResultDetail = {
  ok?: boolean;
  kind?: string;
  format?: ExportFormat | string;

  // correlation (preferred if present)
  requestId?: string;

  blob?: Blob;
  data?: unknown;
  text?: string;
  error?: string;
  message?: string;
  filename?: string;
  mime?: string;

  // optional UI hints
  ui?: {
    autoDownload?: boolean;
    source?: string;
  };
};

const OMNICINE_EXPORT_EVENT = "omnicine:export";
const OMNICINE_EXPORT_RESULT_EVENT = "omnicine:export:result";
const OMNICINE_PODCAST_EXPORT_TOGGLE_EVENT = "omnicine:podcastExport:toggle";

// Anchor to the keep-mounted dock toolbar (so it feels native and avoids header collision)
const DEFAULT_ANCHOR_SELECTOR = "#omnicine-timeline-dock-toolbar";

// Spacing inside the preview surface bounds
const ANCHOR_PAD_PX = 12;

type Placement = "anchored" | "inline";

function safeJsonStringify(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

function makeRequestId(): string {
  try {
    const c = globalThis.crypto as Crypto | undefined;
    if (c?.randomUUID) return c.randomUUID();
  } catch {
    // ignore
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function makeFilename(format: ExportFormat, suggested?: string) {
  if (suggested && suggested.trim()) return suggested.trim();
  const ts = new Date().toISOString().replace(/[:.]/g, "-");
  return format === "wav" ? `podcast-export_${ts}.wav` : `podcast-export_${ts}.json`;
}

function coerceBlob(
  detail: ExportResultDetail,
): { blob: Blob; format: ExportFormat; filename: string } | null {
  const fmt = (detail.format === "wav"
    ? "wav"
    : detail.format === "json"
      ? "json"
      : null) as ExportFormat | null;

  if (detail.blob instanceof Blob) {
    const format: ExportFormat = fmt ?? (detail.blob.type?.includes("audio") ? "wav" : "json");
    const filename = makeFilename(format, detail.filename);
    return { blob: detail.blob, format, filename };
  }

  if (fmt === "json") {
    const jsonText =
      typeof detail.text === "string"
        ? detail.text
        : detail.data !== undefined
          ? safeJsonStringify(detail.data)
          : safeJsonStringify(detail);

    const blob = new Blob([jsonText], { type: detail.mime || "application/json" });
    const filename = makeFilename("json", detail.filename);
    return { blob, format: "json", filename };
  }

  return null;
}

type ChipTone = "cyan" | "emerald" | "violet" | "amber" | "red" | "slate";

function chipCls(tone: ChipTone): string {
  switch (tone) {
    case "cyan":
      return "border-cyan-300/20 bg-cyan-500/15 text-cyan-100";
    case "emerald":
      return "border-emerald-300/20 bg-emerald-500/15 text-emerald-100";
    case "violet":
      return "border-violet-300/20 bg-violet-500/15 text-violet-100";
    case "amber":
      return "border-amber-300/20 bg-amber-500/15 text-amber-100";
    case "red":
      return "border-red-300/20 bg-red-500/15 text-red-100";
    default:
      return "border-white/10 bg-white/10 text-white/80";
  }
}

function Chip(props: { tone?: ChipTone; children: React.ReactNode; className?: string }) {
  const tone = props.tone ?? "slate";
  return (
    <span
      className={[
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium",
        chipCls(tone),
        props.className || "",
      ].join(" ")}
    >
      {props.children}
    </span>
  );
}

type AnchorPos = {
  top: number;
  left: number;
  ok: boolean;
  align: "top-right" | "center-right";
};

/**
 * Anchor position reader (enabled/disabled).
 * NOTE: We cannot conditionally call hooks, so we keep a single hook and
 * make it inert when placement !== "anchored".
 */
function useAnchorPosition(
  selector: string,
  align: "top-right" | "center-right",
  enabled: boolean,
): AnchorPos {
  const [pos, setPos] = React.useState<AnchorPos>({ top: 0, left: 0, ok: false, align });

  React.useEffect(() => {
    if (!enabled) {
      setPos((p) => (p.ok ? { top: 0, left: 0, ok: false, align } : { ...p, ok: false, align }));
      return;
    }

    let raf = 0;
    let ro: ResizeObserver | null = null;

    const read = () => {
      const el = document.querySelector(selector) as HTMLElement | null;
      if (!el) {
        setPos((p) => (p.ok ? { top: 0, left: 0, ok: false, align } : p));
        return;
      }

      const r = el.getBoundingClientRect();

      // left is the *right edge* anchor; we translateX(-100%) for right-align.
      const top =
        align === "center-right"
          ? Math.max(0, r.top + r.height / 2)
          : Math.max(0, r.top + ANCHOR_PAD_PX);

      const left = Math.max(0, r.right - ANCHOR_PAD_PX);

      setPos({ top, left, ok: true, align });
    };

    const schedule = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        read();
      });
    };

    // Initial read
    read();

    // Update on scroll/resize
    window.addEventListener("resize", schedule);
    // Capture phase catches scrolling in nested containers
    window.addEventListener("scroll", schedule, true);

    // Observe anchor resize
    const anchorEl = document.querySelector(selector) as HTMLElement | null;
    if (anchorEl && "ResizeObserver" in window) {
      ro = new ResizeObserver(() => schedule());
      ro.observe(anchorEl);
    }

    // If the anchor mounts later (route transition), poll briefly
    const poll = window.setInterval(() => {
      const el = document.querySelector(selector) as HTMLElement | null;
      if (el) {
        schedule();
        window.clearInterval(poll);
      }
    }, 250);

    return () => {
      window.clearInterval(poll);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("scroll", schedule, true);
      if (ro) ro.disconnect();
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [selector, align, enabled]);

  return pos;
}

export default function PodcastExportPanel(props: {
  anchorSelector?: string;
  placement?: Placement;
}): JSX.Element | null {
  // ✅ SAFETY DEFAULT:
  // If you render <PodcastExportPanel /> in the OmniCine header, it MUST behave inline.
  const placement = props.placement ?? "inline";
  const isInline = placement === "inline";

  const anchorSelector = props.anchorSelector ?? DEFAULT_ANCHOR_SELECTOR;

  // Only compute anchor when we're in anchored mode (hook is inert otherwise).
  const anchor = useAnchorPosition(anchorSelector, "center-right", !isInline);

  const [collapsed, setCollapsed] = React.useState<boolean>(true);

  const audioRef = React.useRef<HTMLAudioElement | null>(null);

  // Allow OmniCine toolbar button to toggle panel open/closed (optional external trigger)
  React.useEffect(() => {
    const onToggle = () => setCollapsed((v) => !v);
    window.addEventListener(OMNICINE_PODCAST_EXPORT_TOGGLE_EVENT, onToggle as EventListener);
    return () =>
      window.removeEventListener(OMNICINE_PODCAST_EXPORT_TOGGLE_EVENT, onToggle as EventListener);
  }, []);

  const [busy, setBusy] = React.useState<ExportFormat | null>(null);
  const [status, setStatus] = React.useState<string>(
    "Ready. Uses ExportBridge + installed resolvers.",
  );
  const [error, setError] = React.useState<string | null>(null);

  const [jsonUrl, setJsonUrl] = React.useState<string | null>(null);
  const [jsonName, setJsonName] = React.useState<string | null>(null);

  const [wavUrl, setWavUrl] = React.useState<string | null>(null);
  const [wavName, setWavName] = React.useState<string | null>(null);

  const [progressText, setProgressText] = React.useState<string>("");

  // Correlate results to last request (prevents unrelated results reopening panel)
  const [pendingRequestId, setPendingRequestId] = React.useState<string | null>(null);

  const stopAudio = React.useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    try {
      a.pause();
      a.removeAttribute("src");
      a.load();
    } catch {
      // ignore
    }
  }, []);

  const clear = React.useCallback(() => {
    setBusy(null);
    setError(null);
    setStatus("Ready. Uses ExportBridge + installed resolvers.");
    setProgressText("");
    setPendingRequestId(null);

    stopAudio();

    setJsonUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setWavUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });

    setJsonName(null);
    setWavName(null);
    setCollapsed(true);
  }, [stopAudio]);

  // Cleanup any remaining object URLs on unmount
  React.useEffect(() => {
    return () => {
      try {
        stopAudio();
      } catch {
        // ignore
      }
      if (jsonUrl) URL.revokeObjectURL(jsonUrl);
      if (wavUrl) URL.revokeObjectURL(wavUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // IMPORTANT FIX:
  // If anchored mode loses its anchor (timeline closed), hard-clear and hide.
  const prevAnchorOkRef = React.useRef<boolean>(anchor.ok);
  React.useEffect(() => {
    const prev = prevAnchorOkRef.current;
    prevAnchorOkRef.current = anchor.ok;

    if (!isInline && prev && !anchor.ok) {
      clear();
    }
  }, [anchor.ok, isInline, clear]);

  const dispatchExport = React.useCallback((format: ExportFormat) => {
    const requestId = makeRequestId();

    setCollapsed(false);
    setBusy(format);
    setError(null);
    setProgressText("");
    setStatus(`Dispatching export: podcast.v1 → ${format.toUpperCase()} …`);
    setPendingRequestId(requestId);

    window.dispatchEvent(
      new CustomEvent(OMNICINE_EXPORT_EVENT, {
        detail: {
          requestId,
          kind: "podcast.v1",
          format,
          // UI hint: any downloader must NOT auto-download for this request
          ui: { autoDownload: false, source: "PodcastExportPanel" },
        },
      }),
    );
  }, []);

  React.useEffect(() => {
    const onResult = (e: Event) => {
      const detail = (e as CustomEvent).detail as ExportResultDetail | undefined;
      if (!detail) return;
      if (detail.kind && detail.kind !== "podcast.v1") return;

      // ✅ Strict correlation:
      // If we issued a requestId, we only accept results that echo it.
      if (pendingRequestId) {
        if (!detail.requestId) return;
        if (detail.requestId !== pendingRequestId) return;
      }

      setBusy(null);
      setPendingRequestId(null);

      const ok = detail.ok !== false && !detail.error;
      if (!ok) {
        setError(detail.error || "Export failed.");
        setStatus(detail.message || "Export failed.");
        setCollapsed(false);
        return;
      }

      const coerced = coerceBlob(detail);
      if (!coerced) {
        setStatus("Export completed, but no usable payload was returned.");
        setCollapsed(false);
        return;
      }

      const { blob, format, filename } = coerced;
      const url = URL.createObjectURL(blob);

      if (format === "json") {
        setJsonUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
        setJsonName(filename);
        setStatus("JSON export ready.");
      } else {
        // stop current playback before swapping
        stopAudio();
        setWavUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
        setWavName(filename);
        setStatus("WAV render ready.");
      }

      setCollapsed(false);
    };

    window.addEventListener(OMNICINE_EXPORT_RESULT_EVENT, onResult as EventListener);
    return () =>
      window.removeEventListener(OMNICINE_EXPORT_RESULT_EVENT, onResult as EventListener);
  }, [pendingRequestId, stopAudio]);

  React.useEffect(() => {
    const t = window.setInterval(() => {
      const p = window.__oc_export?.lastProgress;
      if (p == null) {
        setProgressText("");
        return;
      }

      if (typeof p === "number" && Number.isFinite(p)) {
        const pct = p <= 1 ? Math.round(p * 100) : Math.round(p);
        setProgressText(`Progress ${pct}%`);
        return;
      }

      if (typeof p === "object") {
        const anyP = p as any;
        const rawPct =
          (typeof anyP.pct === "number" && Number.isFinite(anyP.pct) && anyP.pct) ||
          (typeof anyP.progress === "number" &&
            Number.isFinite(anyP.progress) &&
            anyP.progress) ||
          null;

        const phase =
          (typeof anyP.phase === "string" && anyP.phase) ||
          (typeof anyP.stage === "string" && anyP.stage) ||
          "";

        if (rawPct != null) {
          const pct = rawPct <= 1 ? Math.round(rawPct * 100) : Math.round(rawPct);
          setProgressText(phase ? `${phase} ${pct}%` : `Progress ${pct}%`);
          return;
        }

        setProgressText(String(phase || "Progress"));
        return;
      }

      setProgressText(String(p));
    }, 200);

    return () => window.clearInterval(t);
  }, []);

  // --- wrapper positioning ---
  // ✅ In anchored mode, if anchor isn't present, unmount entirely (NO bottom-right fallback)
  if (!isInline && !anchor.ok) return null;

  const wrapperStyle: React.CSSProperties | undefined = isInline
    ? undefined
    : { position: "fixed", top: anchor.top, left: anchor.left };

  const wrapperCls = isInline
    ? "relative inline-flex items-center"
    : "pointer-events-none z-[9999] transform -translate-x-full -translate-y-1/2";

  const ExpandedPanelContent = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Chip tone="cyan">Podcast Export</Chip>
            {busy ? (
              <Chip tone="amber">{busy === "wav" ? "Rendering…" : "Exporting…"}</Chip>
            ) : error ? (
              <Chip tone="red">Error</Chip>
            ) : (
              <Chip tone="emerald">Ready</Chip>
            )}
            {progressText ? <Chip tone="amber">{progressText}</Chip> : null}
          </div>

          <div className="mt-2 text-xs text-white/60 line-clamp-2">{status}</div>

          <div className="mt-1 text-[11px] text-white/40">
            <span className="font-mono">omnicine:export</span> →{" "}
            <span className="font-mono">omnicine:export:result</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={clear}
            className="rounded-full bg-white/10 px-2 py-1 text-xs text-white/70 hover:bg-white/15"
            title="Clear output"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            className="rounded-full bg-white/10 px-2 py-1 text-xs text-white/70 hover:bg-white/15"
            title="Collapse"
            disabled={!!busy}
          >
            –
          </button>
        </div>
      </div>

      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={() => dispatchExport("json")}
          disabled={!!busy}
          className="flex-1 rounded-xl bg-white/10 px-3 py-2 text-sm font-medium text-white/85 hover:bg-white/15 disabled:opacity-50"
        >
          <span className="inline-flex items-center gap-2">
            <Chip tone="violet" className="px-2 py-0.5">
              JSON
            </Chip>
            {busy === "json" ? "Exporting…" : "Export"}
          </span>
        </button>

        <button
          type="button"
          onClick={() => dispatchExport("wav")}
          disabled={!!busy}
          className="flex-1 rounded-xl bg-white/10 px-3 py-2 text-sm font-medium text-white/85 hover:bg-white/15 disabled:opacity-50"
        >
          <span className="inline-flex items-center gap-2">
            <Chip tone="violet" className="px-2 py-0.5">
              WAV
            </Chip>
            {busy === "wav" ? "Rendering…" : "Render"}
          </span>
        </button>
      </div>

      {error ? (
        <div className="mt-2 rounded-2xl border border-red-300/20 bg-red-500/10 p-3 text-xs text-red-100">
          {error}
        </div>
      ) : null}

      {jsonUrl || wavUrl ? (
        <div className="mt-3 space-y-3">
          {jsonUrl ? (
            <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
              <div className="flex items-center justify-between">
                <Chip tone="violet">JSON</Chip>
                {jsonName ? (
                  <span className="text-[11px] text-white/40 truncate max-w-[240px]">
                    {jsonName}
                  </span>
                ) : null}
              </div>
              <div className="mt-2 flex items-center justify-between gap-2">
                <a
                  href={jsonUrl}
                  download={jsonName ?? undefined}
                  className="rounded-full bg-white/10 px-3 py-2 text-xs text-white/80 hover:bg-white/15"
                >
                  Download
                </a>
                <a
                  href={jsonUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-white/50 hover:text-white/70"
                >
                  Open
                </a>
              </div>
            </div>
          ) : null}

          {wavUrl ? (
            <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
              <div className="flex items-center justify-between">
                <Chip tone="violet">WAV</Chip>
                {wavName ? (
                  <span className="text-[11px] text-white/40 truncate max-w-[240px]">{wavName}</span>
                ) : null}
              </div>
              <div className="mt-2">
                <audio ref={audioRef} className="w-full" controls src={wavUrl} />
              </div>
              <div className="mt-2 flex items-center justify-end">
                <a
                  href={wavUrl}
                  download={wavName ?? undefined}
                  className="rounded-full bg-white/10 px-3 py-2 text-xs text-white/80 hover:bg-white/15"
                >
                  Download
                </a>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );

  return (
    <div className={wrapperCls} style={wrapperStyle} data-podcast-export-panel="1">
      {/* Trigger button (ALWAYS in-flow) */}
      <div className={isInline ? "" : "pointer-events-auto"}>
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          className={[
            "h-9 rounded-full px-3",
            "border border-white/12 bg-white/[0.08] backdrop-blur-xl",
            "text-[12px] font-medium text-white/85",
            isInline ? "shadow-none" : "shadow-[0_12px_30px_rgba(0,0,0,0.35)]",
            "hover:bg-white/[0.10] hover:border-white/18",
            "focus-visible:outline focus-visible:outline-2 focus-visible:outline-cyan-400/70",
          ].join(" ")}
          title="Podcast Export"
        >
          <div className="flex items-center gap-2">
            <span className="text-white/90">Podcast Export</span>
            <span className="text-white/25">·</span>
            {busy ? (
              <span className="text-white/70">{busy === "wav" ? "Rendering…" : "Exporting…"}</span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-white/70">
                <span className="h-2 w-2 rounded-full bg-emerald-400/80 ring-1 ring-emerald-300/25" />
                Ready
              </span>
            )}
          </div>
        </button>
      </div>

      {/* Expanded panel */}
      {!collapsed ? (
        isInline ? (
          <div
            className={[
              "absolute right-0 top-full mt-2",
              "z-[2147483647] pointer-events-auto",
              "w-[360px] rounded-2xl border border-white/10 bg-black/55 p-3 shadow-2xl backdrop-blur-xl",
            ].join(" ")}
          >
            {ExpandedPanelContent}
          </div>
        ) : (
          <div className="pointer-events-auto w-[360px] rounded-2xl border border-white/10 bg-black/55 p-3 shadow-2xl backdrop-blur-xl">
            {ExpandedPanelContent}
          </div>
        )
      ) : null}
    </div>
  );
}
